import { ReactElement } from 'react';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { TextFieldProps, IconButton, InputAdornment } from '@mui/material';
import {
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
} from 'react-hook-form';
import { useBoolean } from 'usehooks-ts';

import { withStylesSvgIcon } from '../../hoc';
import { CustomTextField } from '../custom/CustomTextField';

const StyledVisibility = withStylesSvgIcon(Visibility);
const StyledVisibilityOff = withStylesSvgIcon(VisibilityOff);

// New type without deprecated InputProps
type ModifiedTextFieldProps = Omit<
  TextFieldProps,
  'inputRef' | 'InputProps' | 'type'
> & {
  slotProps?: TextFieldProps['slotProps'];
};

export type PasswordFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = ModifiedTextFieldProps & {
  ControllerProps: Omit<ControllerProps<TFieldValues, TName>, 'render'>;
};

export const PasswordField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ControllerProps,
  ...props
}: PasswordFieldProps<TFieldValues, TName>): ReactElement => {
  const { value: showPassword, toggle: toggleShowPassword } = useBoolean(false);
  const { value: isFocused, toggle: toggleFocusState } = useBoolean(false);

  return (
    <Controller
      {...ControllerProps}
      render={({
        field: { ref, onChange, ...field },
        fieldState: { error },
      }) => (
        <CustomTextField
          {...props}
          {...field}
          inputRef={ref}
          type={showPassword ? 'text' : 'password'}
          error={props.error || !!error}
          helperText={error?.message || props.helperText}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={toggleShowPassword}
                    tabIndex={-1}
                    disableRipple
                    disableFocusRipple
                    sx={{
                      '&:focus': { outline: 'none', boxShadow: 'none' },
                    }}
                  >
                    {showPassword ? (
                      <StyledVisibility isFocused={isFocused} />
                    ) : (
                      <StyledVisibilityOff isFocused={isFocused} />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
              ...props.slotProps,
            },
          }}
          onChange={(event) => {
            onChange(event);
            props.onChange?.(event);
          }}
          onFocus={(event) => {
            toggleFocusState();
            props.onFocus?.(event);
          }}
          onBlur={(event) => {
            toggleFocusState();
            props.onBlur?.(event);
          }}
        />
      )}
    />
  );
};
